import { SelectedItem } from '@/components/treemenu/TreeMenu';
import { Application } from '@/types/application';
import { Connection } from '@/types/connection';
import { Subroutine, SubroutineFolder } from '@/types/subroutine';
import { getHighestNumber } from '@/util/number';
import { findSubroutine, getNewBlockId } from './application';
import { copySubroutinesToClipboard } from './clipboard';
import { isNumber } from './typeGuards';

const generateNewIDs = (selectedNodeIds: number[], application: Application): Record<string, number> => {
  const idMapping: Record<string, number> = {};
  selectedNodeIds.forEach((oldId, index) => {
    const newId = getNewBlockId(application);
    idMapping[oldId] = newId + index;
  });
  return idMapping;
};

const updateConnections = (
  filteredConnections: Record<string, Connection[]>,
  idMapping: Record<string, number>
): Record<string, Connection[]> => {
  const updatedConnections = structuredClone(filteredConnections);
  for (const [srcId, connections] of Object.entries(filteredConnections)) {
    const newSrcId = idMapping[srcId];
    updatedConnections[newSrcId] = [];
    connections?.forEach((conn, index) => {
      updatedConnections[newSrcId][index] = {
        ...conn,
        dst: isNumber(conn.dst) ? (idMapping[conn.dst] ?? null) : null
      };
    });
  }
  return updatedConnections;
};

const duplicateSubroutine = (
  application: Application,
  selectedSubroutineId: number,
  folderId?: number,
  isTopLevel?: boolean
) => {
  const newApplication = application;
  const currentSubroutine = findSubroutine(newApplication.subroutines, selectedSubroutineId);
  if (!currentSubroutine) return application;

  const newSubroutineId = getHighestNumber(newApplication.subroutines, 'id') + 1;
  const selectedNodeIds = currentSubroutine.nodes;

  const filteredConnections = Object.fromEntries(
    selectedNodeIds.map((nodeId) => [[nodeId], newApplication.blocks.connections[nodeId]])
  );
  const idMapping: Record<string, number> = generateNewIDs(selectedNodeIds, newApplication);
  const updatedConnections = updateConnections(filteredConnections, idMapping);

  const newIds: number[] = [];

  currentSubroutine.nodes.map((node) => {
    const newId = idMapping[node];
    if (newApplication.blocks.nodes[newId]) return;

    newIds.push(newId);

    newApplication.blocks.nodes[newId] = newApplication.blocks.nodes[node];
    newApplication.blocks.details[newId] = newApplication.blocks.details[node];
    newApplication.blocks.connections[newId] = updatedConnections[newId];
    if (newApplication.blocks.layouts[node]) {
      newApplication.blocks.layouts[newId] = { ...newApplication.blocks.layouts[node] };
    }
    if (newApplication.blocks.portoptions[node]) {
      newApplication.blocks.portoptions[newId] = newApplication.blocks.portoptions[node];
    }
    if (newApplication.blocks.event_handlers && newApplication.blocks.event_handlers[node]) {
      newApplication.blocks.event_handlers[newId] = newApplication.blocks.event_handlers[node];
    }
  });

  newApplication.subroutines.push({
    ...currentSubroutine,
    id: newSubroutineId,
    label: isTopLevel ? currentSubroutine.label + ' - Copy' : currentSubroutine.label,
    nodes: newIds,
    folder: folderId,
    startblock: idMapping[currentSubroutine.startblock || 0],
    args: currentSubroutine.args.map((arg) => ({ ...arg })),
    arg_bindings: currentSubroutine.arg_bindings.slice(),
    event_args: currentSubroutine.event_args?.map((eventArg) => ({ ...eventArg })),
    event_arg_bindings: currentSubroutine.event_arg_bindings?.slice(),
    return_types: currentSubroutine.return_types.map((returnType) => ({ ...returnType }))
  });

  newApplication.variables.local[newSubroutineId] = newApplication.variables.local[selectedSubroutineId]?.map(
    (variable) => ({ ...variable })
  );

  return newApplication;
};

const duplicateFolder = (
  application: Application,
  selectedFolderId: number,
  folderId?: number,
  isTopLevel?: boolean
) => {
  const currentFolder = application.folders?.find((folder) => folder.id === selectedFolderId);
  const newFolder = {
    id: getHighestNumber(application.folders || [], 'id') + 1,
    label: (currentFolder?.label || '') + (isTopLevel ? ' - Copy' : ''),
    parentId: folderId || 0
  };

  application.folders?.push(newFolder);
  application.subroutines.forEach((subroutine) => {
    if (subroutine.folder === selectedFolderId) {
      application = duplicateSubroutine(application, subroutine.id, newFolder.id);
    }
  });

  application.folders?.forEach((folder) => {
    if (folder.parentId === selectedFolderId) {
      application = duplicateFolder(application, folder.id, newFolder.id);
    }
  });

  return application;
};

export const duplicateItem = (application: Application, selectedItem: SelectedItem, folderId?: number) => {
  if (!selectedItem.id) return;
  if (selectedItem.type === 'subroutine') {
    return duplicateSubroutine(structuredClone(application), selectedItem.id, folderId, true);
  }

  if (selectedItem.type === 'folder') {
    return duplicateFolder(structuredClone(application), selectedItem.id, folderId, true);
  }
};

const getFoldersAndSubroutines = (
  folderId: number,
  folders: SubroutineFolder[],
  subroutines: Subroutine[],
  value?: { folders: SubroutineFolder[]; subroutines: Subroutine[] }
) => {
  let result = value ?? { folders: [], subroutines: [] };

  const relevantSubroutines = subroutines.filter((subroutine) => subroutine.folder === folderId);
  result.subroutines.push(...relevantSubroutines);

  const subFolders = folders.filter(({ parentId }) => parentId === folderId);
  if (subFolders.length) {
    result.folders.push(...subFolders);
    subFolders.forEach((subFolder) => {
      result = { ...result, ...getFoldersAndSubroutines(subFolder.id, folders, subroutines, result) };
    });
  }

  return result;
};

export const copyItem = (application: Application, selectedItem: SelectedItem, folderId?: number): Promise<number> => {
  const subroutines: Subroutine[] = [];
  const folders: SubroutineFolder[] = [];
  const mainFolder = application.folders?.find(({ id }) => id === folderId);

  if (selectedItem.id) {
    const currentSubroutine = findSubroutine(application.subroutines, selectedItem.id);
    currentSubroutine && subroutines.push({ ...currentSubroutine, folder: undefined });
  } else {
    const data = getFoldersAndSubroutines(mainFolder?.id || 0, application.folders ?? [], application.subroutines);

    subroutines.push(...data.subroutines);

    mainFolder && folders.push(mainFolder);
    folders.push(...data.folders);
  }

  return copySubroutinesToClipboard(application, subroutines, folders);
};
